/**
 * itemCostSummary — the four separate money concepts a canonical inventory
 * item carries, kept deliberately apart so no screen can collapse them into
 * one ambiguous "Price" or "Vendor" field:
 *
 *   Current inventory cost  what we value inventory and recipes at, per the
 *                           item's own count unit, under a stated costing method
 *   Preferred vendor        the purchasing preference — NOT necessarily the
 *                           cheapest, newest, or most recently used
 *   Last purchased          the most recent COMPLETED purchase (invoice,
 *                           receipt, confirmed shopping trip, confirmed
 *                           allocation) — never a draft or a quote
 *   Best comparable price   the lowest normalized price among options whose
 *                           pack/unit conversion actually resolves
 *
 * Everything here is pure: callers hand in a canonical item, its
 * purchase_options rows, and (optionally) normalized purchase records; they
 * get back plain data. Money is returned UNROUNDED — rounding is a display
 * concern, and rounding before comparison is how two different prices come to
 * look tied. Missing data is reported as `available: false` plus a reason
 * code; it is never reported as 0.
 *
 * The CURRENT INVENTORY COST half of this file is not implemented here: it
 * lives in `supabase/functions/_shared/canonicalItemCost.mjs` and is imported
 * below, because the recipe-costing edge function values plates with the same
 * policy. One module, two runtimes — not a browser copy and a server copy that
 * can drift. Everything else on this page (freshness, best comparable price,
 * option summaries, display formatting) is browser-facing and stays here.
 */

// Which vendor product an order is placed against is ONE implementation for
// both runtimes — the server's count→PO bucket builder asks the same question
// (see _shared/orderOptionSelection.mjs). Re-exported below, not re-written.
import {
  offerableOptions,
  resolveSelectedOption,
} from '../../../supabase/functions/_shared/orderOptionSelection.mjs';
import {
  COSTING_METHODS,
  DEFAULT_COSTING_METHOD,
  resolveCostingMethod,
  COST_UNAVAILABLE_REASONS,
  LEGACY_COST_SOURCE,
  SUB_RECIPE_COST_SOURCE,
  activeOptions,
  preferredOption,
  comparableUnitCost,
  vendorLabelOf,
  COMPLETED_PURCHASE_KINDS,
  isCompletedPurchase,
  purchaseRecordsFromOptions,
  purchaseRecordsFromPriceHistory,
  purchaseRecordsFromAllocationLines,
  lastPurchaseOf,
  weightedAverageUnitCost,
  resolveCanonicalInventoryCost,
  currentInventoryCost,
  toFiniteNumber,
  toPositiveNumber,
} from '../../../supabase/functions/_shared/canonicalItemCost.mjs';

export {
  COSTING_METHODS,
  DEFAULT_COSTING_METHOD,
  resolveCostingMethod,
  COST_UNAVAILABLE_REASONS,
  LEGACY_COST_SOURCE,
  SUB_RECIPE_COST_SOURCE,
  activeOptions,
  preferredOption,
  comparableUnitCost,
  vendorLabelOf,
  COMPLETED_PURCHASE_KINDS,
  isCompletedPurchase,
  purchaseRecordsFromOptions,
  purchaseRecordsFromPriceHistory,
  purchaseRecordsFromAllocationLines,
  lastPurchaseOf,
  weightedAverageUnitCost,
  resolveCanonicalInventoryCost,
  currentInventoryCost,
};

const timeOf = (value) => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Days since `value`, or null when there is no usable date. */
export function ageInDays(value, now = Date.now()) {
  const time = timeOf(value);
  if (time === null) return null;
  return Math.max(0, Math.floor((now - time) / 86400000));
}

/**
 * A vendor price older than this is disclosed as possibly outdated. Shared
 * with PurchaseOptionsEditor so one number governs every freshness label.
 */
export const STALE_PRICE_DAYS = 90;

/** True when an option's price is outside the accepted freshness window. */
export function isPriceStale(option, now = Date.now()) {
  const age = ageInDays(option?.effective_date ?? option?.updated_at, now);
  return age !== null && age > STALE_PRICE_DAYS;
}

/* ── Purchase options ────────────────────────────────────────────────────── */

// Eligibility for buying, and which product an order is placed against, are
// the same two answers the server's direct-order path needs, so both live in
// `_shared/orderOptionSelection.mjs` and are re-exported here unchanged. A
// browser copy of a selection rule is exactly how the Ordering Hub preview
// and the created draft came to disagree.
export { offerableOptions, resolveSelectedOption };

const vendorKeyOf = (option) =>
  option?.vendor_id ?? option?.vendor_name_text ?? option?.vendor_description ?? null;

/** Count of distinct vendors across the given options. */
export function distinctVendorCount(options) {
  const keys = new Set();
  (options || []).forEach((option) => {
    const key = vendorKeyOf(option);
    if (key !== null && key !== undefined && key !== '') keys.add(String(key));
  });
  return keys.size;
}

/**
 * Whether an item is acting as a CANONICAL parent — one countable item with
 * several vendor products grouped underneath it — and how to say so in a
 * badge.
 *
 * Two packs from the SAME vendor still count: they are connected products a
 * counter may want to review, even though there is no vendor choice to make.
 * A lone legacy mirror option does not — every item has one of those, and a
 * badge on every row says nothing.
 *
 * Under link-don't-merge, "several vendor products on one item" stopped
 * being the shape multi-vendor reality takes: same-use products land as
 * SEPARATE items joined in a working-set relationship group, each holding
 * one option, so optionCount alone is a dead signal. Callers that have the
 * group should pass the group-POOLED options (relatedItemGroups
 * expandOptionsAcrossGroup) — the vendor arithmetic then just works — and
 * `context.groupMemberCount` (members in the item's working-set group,
 * itself included) so membership alone also qualifies when a sibling's
 * options could not be pooled.
 */
export function canonicalLinkage(options, context = {}) {
  const active = activeOptions(options);
  const vendorCount = distinctVendorCount(active);
  const optionCount = active.length;
  const groupMemberCount = Number(context?.groupMemberCount) || 0;
  const grouped = groupMemberCount >= 2;
  const multiVendor = vendorCount >= 2;
  return {
    isCanonical: optionCount >= 2 || multiVendor || grouped,
    multiVendor,
    optionCount,
    vendorCount,
    groupMemberCount,
    badgeLabel: multiVendor
      ? 'Multi-vendor'
      : (grouped
        ? `${groupMemberCount} related products`
        : `${optionCount} linked product${optionCount === 1 ? '' : 's'}`),
    headingLabel: multiVendor
      ? `Available from ${vendorCount} vendors`
      : (grouped
        ? `${groupMemberCount} related products`
        : `${optionCount} linked vendor product${optionCount === 1 ? '' : 's'}`),
  };
}

/**
 * Lowest comparable price among offerable options, per the item's COUNT unit.
 *
 * Comparison uses UNROUNDED values, so two prices that merely round to the
 * same displayed figure still produce a single winner. A genuine tie returns
 * every tied option so the caller can say "shared by N vendors" instead of
 * picking one arbitrarily.
 *
 * Not the vendor-selection rule: that is selectAutomaticOption over the
 * relationship comparison (utils/priceComparison.js), which prices in the
 * relationship's practical unit and gates AI estimates by confidence. This
 * stays on the count unit for the item header's "best price" figure, where
 * the count unit is the unit everything else on the screen is stated in.
 *
 * @returns {{option, unitCost, tied: boolean, tiedOptions: Array,
 *            tiedVendorCount: number, stale: boolean}|null}
 */
export function bestComparableOffer(options, now = Date.now()) {
  let best = null;
  let tiedOptions = [];

  offerableOptions(options).forEach((option) => {
    const cost = comparableUnitCost(option);
    if (cost === null) return;
    if (best === null || cost < best.unitCost) {
      best = { option, unitCost: cost };
      tiedOptions = [option];
    } else if (cost === best.unitCost) {
      tiedOptions.push(option);
    }
  });

  if (best === null) return null;
  return {
    option: best.option,
    unitCost: best.unitCost,
    tied: tiedOptions.length > 1,
    tiedOptions,
    tiedVendorCount: distinctVendorCount(tiedOptions),
    stale: isPriceStale(best.option, now),
  };
}

/**
 * Counts and price range across an item's linked vendor products.
 *
 * `needsReview` is never folded into `comparable` — an option we cannot
 * normalize stays visible as unresolved work, not hidden to make the summary
 * look tidy.
 */
export function purchaseOptionsSummary(options, now = Date.now()) {
  const active = activeOptions(options);
  const offerable = offerableOptions(options);

  const costs = [];
  let needsReview = 0;
  active.forEach((option) => {
    const cost = comparableUnitCost(option);
    if (cost === null) needsReview += 1;
    else costs.push(cost);
  });

  const stale = active.filter((option) => isPriceStale(option, now)).length;

  // What each vendor charges for one of ITS packages. A separate question from
  // the per-unit range above, and a coarser one: a Costco tub and a Sysco case
  // are different amounts of goods, so these numbers are not strictly
  // comparable to each other. They are still worth showing, because an item
  // whose packages nobody has sized yet has no per-unit range at all, and a
  // dash tells the user nothing about a price we plainly know.
  const packagePrices = active
    .map((option) => Number(option?.package_price))
    .filter((n) => Number.isFinite(n) && n > 0);

  // Only claim one order unit when every vendor genuinely uses it; otherwise
  // the range spans cases and tubs and must not name either.
  const purchaseUnits = [...new Set(
    active.map((o) => String(o?.purchase_unit || '').trim().toLowerCase()).filter(Boolean),
  )];

  return {
    total: active.length,
    vendorCount: distinctVendorCount(active),
    offerableCount: offerable.length,
    comparableCount: costs.length,
    needsReviewCount: needsReview,
    staleCount: stale,
    range: costs.length
      ? { low: Math.min(...costs), high: Math.max(...costs) }
      : null,
    packageRange: packagePrices.length
      ? { low: Math.min(...packagePrices), high: Math.max(...packagePrices) }
      : null,
    packageUnit: purchaseUnits.length === 1 ? purchaseUnits[0] : null,
  };
}

/* ── The whole picture ───────────────────────────────────────────────────── */

/**
 * Everything an item header needs, in one pass. Callers pass whatever they
 * have; each section degrades independently, so a failed price comparison
 * never blanks the current inventory cost (or blocks editing the item).
 *
 * @param {object}   args.item          canonical inventory_items row
 * @param {Array}    args.options       purchase_options rows for the item
 * @param {Array}   [args.priceHistory] price_history rows for the item
 * @param {Array}   [args.allocationLines] purchase_allocation_lines rows
 * @param {object}  [args.store]        store row (costing-method setting)
 * @param {object}  [args.vendorNameById] { [vendorId]: display_name }
 * @param {number}  [args.groupMemberCount] members in the item's working-set
 *                  (EQUIVALENT) group, itself included — see canonicalLinkage.
 *                  Callers with the group pass group-POOLED options AND this
 *                  count, so `linkage` can qualify an item whose sibling's
 *                  options could not be pooled. Defaults to 0: existing
 *                  callers see the exact pre-group behavior.
 */
export function buildItemCostSummary({
  item,
  options,
  priceHistory,
  allocationLines,
  store,
  vendorNameById = {},
  groupMemberCount = 0,
  now = Date.now(),
} = {}) {
  const all = options || [];
  const purchases = [
    ...purchaseRecordsFromOptions(all),
    ...purchaseRecordsFromPriceHistory(priceHistory, all),
    ...purchaseRecordsFromAllocationLines(allocationLines),
  ];

  const summary = purchaseOptionsSummary(all, now);
  const preferred = preferredOption(all);
  const best = bestComparableOffer(all, now);
  const last = lastPurchaseOf(purchases);

  const decorate = (option) => (option ? {
    option,
    vendorLabel: vendorLabelOf(option, vendorNameById),
    vendorItemName: option.vendor_description || null,
    packDescription: option.pack_description || option.purchase_unit || null,
    packagePrice: toFiniteNumber(option.package_price),
    unitCost: comparableUnitCost(option),
    effectiveDate: option.effective_date ?? null,
    stale: isPriceStale(option, now),
  } : null);

  const lastPurchase = last ? {
    ...last,
    vendorLabel: last.vendorId != null
      ? (vendorNameById[last.vendorId] || last.vendorNameText || null)
      : (last.vendorNameText || null),
    isPreferred: preferred != null && last.optionId != null
      && String(preferred.id) === String(last.optionId),
  } : null;

  return {
    item: item || null,
    countUnit: item?.unit || null,
    isMultiVendor: summary.vendorCount >= 2,
    // The working-set linkage verdict over the SAME options this summary was
    // built from. With pooled options and groupMemberCount, group membership
    // is readable here without a second derivation at every render site.
    linkage: canonicalLinkage(all, { groupMemberCount }),
    // vendorNameById rides along so the current cost can NAME the vendor
    // product that supplied it. Provenance for the number — never a vendor
    // choice, and never something a recipe inherits.
    currentCost: currentInventoryCost({ item, options: all, purchases, store, vendorNameById, now }),
    preferred: decorate(preferred),
    lastPurchase,
    best: best ? { ...decorate(best.option), unitCost: best.unitCost, tied: best.tied, tiedVendorCount: best.tiedVendorCount, stale: best.stale } : null,
    optionsSummary: summary,
    purchases,
  };
}

/* ── Display helpers ─────────────────────────────────────────────────────── */

/**
 * Money for display. Sub-dime unit costs keep up to 4 decimals (a $0.0359/fl
 * oz price is meaningless at 2), everything else uses 2.
 */
export function formatMoney(value, { precise = false } = {}) {
  const parsed = toFiniteNumber(value);
  if (parsed === null) return null;
  if (precise && Math.abs(parsed) < 0.1) {
    return `$${parsed.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}`;
  }
  return `$${parsed.toFixed(2)}`;
}

/** e.g. '$0.73/lb'. Returns null when either half is missing. */
export function formatUnitPrice(value, unit) {
  const money = formatMoney(value, { precise: true });
  if (money === null) return null;
  return `${money}/${unit || 'unit'}`;
}

/** e.g. '$0.68–$0.85/lb', or a single price when the range is a point. */
export function formatPriceRange(range, unit) {
  if (!range) return null;
  const low = formatMoney(range.low, { precise: true });
  const high = formatMoney(range.high, { precise: true });
  if (low === null || high === null) return null;
  if (low === high) return `${low}/${unit || 'unit'}`;
  return `${low}–${high}/${unit || 'unit'}`;
}

/**
 * The spread of what vendors charge per package, e.g. "$7.50–$11.26/case".
 *
 * Always suffixed with an order unit — "case" when every vendor ships one,
 * "pkg" when they differ. A package price with no unit beside it is the exact
 * ambiguity the unit model exists to remove: "$11.26" could be a case, a tub or
 * a pound, and those are three different orders.
 */
export function formatPackagePriceRange(range, packageUnit) {
  if (!range) return null;
  const low = formatMoney(range.low);
  const high = formatMoney(range.high);
  if (low === null || high === null) return null;
  const unit = (packageUnit || '').trim() || 'pkg';
  return low === high ? `${low}/${unit}` : `${low}–${high}/${unit}`;
}

/**
 * What to put in a canonical item's Cost cell.
 *
 * Prefers the per-count-unit range, because that is the only figure comparable
 * across vendors. Falls back to the package-price range when no package has a
 * confirmed size — a real price the user can act on, clearly labelled as
 * per-package so it cannot be read as a unit cost. Returns null only when
 * there is genuinely no price anywhere.
 *
 * @returns {{ text:string, basis:'unit'|'package' }|null}
 */
export function costCellForSummary(summary, countUnit) {
  if (!summary) return null;
  const perUnit = formatPriceRange(summary.range, countUnit);
  if (perUnit) return { text: perUnit, basis: 'unit' };
  const perPackage = formatPackagePriceRange(summary.packageRange, summary.packageUnit);
  if (perPackage) return { text: perPackage, basis: 'package' };
  return null;
}

/** Uses the app-wide locale date format; null when there is no usable date. */
export function formatPurchaseDate(value) {
  const time = timeOf(value);
  if (time === null) return null;
  return new Date(time).toLocaleDateString();
}

/**
 * One-line count of vendor products, e.g. '4 vendors available', with the
 * unresolved tail disclosed rather than dropped:
 * '4 vendors available · 3 comparable prices · 1 needs unit review'.
 */
export function describePurchaseOptions(summary) {
  if (!summary || summary.total === 0) return 'No vendor products linked';
  const head = `${summary.vendorCount || summary.total} vendor${(summary.vendorCount || summary.total) === 1 ? '' : 's'} available`;
  if (summary.needsReviewCount > 0) {
    return `${head} · ${summary.comparableCount} comparable price${summary.comparableCount === 1 ? '' : 's'} · ${summary.needsReviewCount} needs unit review`;
  }
  return head;
}

/**
 * Heading for the lowest price. When the winning price is outside the
 * freshness window we say "Lowest known price" — calling a stale figure the
 * "best current price" would overstate what we know.
 */
export function bestPriceLabel(best) {
  if (!best) return 'Best comparable price';
  if (best.tied) return `Best comparable price shared by ${best.tiedVendorCount} vendors`;
  return best.stale ? 'Lowest known price' : 'Best comparable price';
}
